import React from 'react';
import { Chip, Box, Typography, Alert } from '@mui/material';
import { useDecodedToken } from '../../utils/useDecodedToken';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import PersonAddIcon from '@mui/icons-material/PersonAdd';

const USER_TYPE_CONFIG = {
  existing_customer: {
    label: 'Full Account Access',
    color: 'success' as const,
    icon: <AccountCircleIcon />,
    description: 'You have full access to all SMEDCO services including accounts, transactions, and statements.'
  },
  loan_applicant: {
    label: 'Loan Tracker',
    color: 'warning' as const,
    icon: <TrackChangesIcon />,
    description: 'You can manage loan applications and make payments. Account features require account verification.'
  },
  new_user: {
    label: 'New Customer',
    color: 'info' as const,
    icon: <PersonAddIcon />,
    description: 'You can create loan applications. Account features require account verification.'
  }
};

interface UserTypeIndicatorProps {
  showDescription?: boolean;
  variant?: 'chip' | 'alert';
}

export const UserTypeIndicator: React.FC<UserTypeIndicatorProps> = ({
  showDescription = false,
  variant = 'chip'
}) => {
  const user = useDecodedToken();

  if (!user) {
    return null;
  }

  // Only grant full access when both a valid accountNumber exists and hasFullAccess is true
  const hasValidAccount = !!user.accountNumber && String(user.accountNumber).trim() !== '' && String(user.accountNumber).toLowerCase() !== 'undefined' && String(user.accountNumber).toLowerCase() !== 'null';
  const isFullAccess = !!user.hasFullAccess && hasValidAccount;

  // Derive user type: full access → existing_customer; tracking → loan_applicant; otherwise new_user
  const userType = isFullAccess
    ? 'existing_customer'
    : user.trackingNumber
      ? 'loan_applicant'
      : 'new_user';
  const config = USER_TYPE_CONFIG[userType];

  if (variant === 'alert') {
    return (
      <Alert 
        severity={config.color === 'success' ? 'success' : config.color === 'warning' ? 'warning' : 'info'}
        icon={config.icon}
        sx={{ mb: 2 }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
          {config.label}
        </Typography>
        {showDescription && (
          <Typography variant="body2">
            {config.description}
          </Typography>
        )}
      </Alert>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Chip
        icon={config.icon}
        label={config.label}
        color={config.color}
        size="small"
        variant="outlined"
      />
      {showDescription && (
        <Typography variant="body2" color="text.secondary">
          {config.description}
        </Typography>
      )}
    </Box>
  );
};

export default UserTypeIndicator;
